import React, { useState, useEffect, useRef } from "react";
import { collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { FaMicrophone, FaStop, FaPlay, FaPause, FaPaperPlane, FaArrowLeft, FaUser, FaRobot, FaTrash } from "react-icons/fa";

const AdminChatScreen = ({ chatId, customerName, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [swipedMessageId, setSwipedMessageId] = useState(null);
  const [deletingMessageId, setDeletingMessageId] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const audioRefs = useRef({});
  const recordingTimerRef = useRef(null);
  const chatBoxRef = useRef(null);
  const messageRefs = useRef({});
  
  // Minimum swipe distance (px)
  const minSwipeDistance = 50;

  useEffect(() => {
    if (!chatId) return;

    const messagesRef = collection(db, "chats", chatId, "messages");
    const q = query(messagesRef, orderBy("timestamp", "asc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMessages(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      // Auto-scroll to bottom
      setTimeout(() => {
        if (chatBoxRef.current) {
          chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
        }
      }, 100);
    });

    return () => unsubscribe();
  }, [chatId]);

  // Convert audio blob to base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Start recording voice
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(chunks, { type: 'audio/webm' });
        const base64Audio = await blobToBase64(audioBlob);
        
        // Send voice message
        await sendVoiceReply(base64Audio);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);

      // Start recording timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("لا يمكن الوصول إلى الميكروفون. يرجى التحقق من الإعدادات.");
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      setRecordingTime(0);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  // Send voice reply
  const sendVoiceReply = async (audioBase64) => {
    if (!audioBase64 || !chatId) return;

    await addDoc(collection(db, "chats", chatId, "messages"), {
      audioData: audioBase64,
      messageType: "voice",
      sender: "admin",
      timestamp: serverTimestamp(),
      text: "🎤 رسالة صوتية"
    });

    setReply("");
  };

  const sendReply = async () => {
    if (!reply.trim() || !chatId) return;

    await addDoc(collection(db, "chats", chatId, "messages"), {
      text: reply,
      messageType: "text",
      sender: "admin",
      timestamp: serverTimestamp(),
    });

    setReply("");
  };

  // Play/pause audio
  const toggleAudio = (messageId, audioData) => {
    if (!audioData) return;

    const audioElement = audioRefs.current[messageId];
    
    if (playingAudioId === messageId && audioElement) {
      // Pause current audio
      audioElement.pause();
      setPlayingAudioId(null);
    } else {
      // Stop any other playing audio
      Object.values(audioRefs.current).forEach(audio => {
        if (audio && !audio.paused) {
          audio.pause();
          audio.currentTime = 0;
        }
      });

      // Play new audio
      if (!audioElement) {
        const audio = new Audio(audioData);
        audioRefs.current[messageId] = audio;
        audio.onended = () => setPlayingAudioId(null);
        audio.play();
      } else {
        audioElement.play();
      }
      setPlayingAudioId(messageId);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (mediaRecorder?.state === 'recording') {
        mediaRecorder.stop();
      }
      Object.values(audioRefs.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
    };
  }, [mediaRecorder]);

  // Mark chat as read when component mounts
  useEffect(() => {
    if (chatId) {
      const chatRef = doc(db, "chats", chatId);
      updateDoc(chatRef, { unread: false }).catch(console.error);
    }
  }, [chatId]);

  const isMobile = () => window.innerWidth <= 768 || 'ontouchstart' in window;

  // Close swiped message when clicking outside (mobile only)
  useEffect(() => {
    if (!isMobile() || !swipedMessageId) return;
    
    const handleClickOutside = (e) => {
      // Check if click is outside the message wrapper
      const messageWrapper = e.target.closest('.message-wrapper');
      if (!messageWrapper || messageWrapper.dataset.messageId !== swipedMessageId) {
        setSwipedMessageId(null);
      }
    };
    
    document.addEventListener('touchstart', handleClickOutside);
    return () => document.removeEventListener('touchstart', handleClickOutside);
  }, [swipedMessageId, isMobile]);

  // Delete message
  const deleteMessage = async (messageId) => {
    if (!messageId || !chatId) return;
    
    if (window.confirm("هل أنت متأكد من حذف هذه الرسالة؟")) {
      setDeletingMessageId(messageId);
      try {
        await deleteDoc(doc(db, "chats", chatId, "messages", messageId));
        setSwipedMessageId(null);
      } catch (error) {
        console.error("Error deleting message:", error);
        alert("حدث خطأ أثناء حذف الرسالة");
      } finally {
        setDeletingMessageId(null);
      }
    }
  };

  // Touch handlers for swipe gesture
  const onTouchStart = (e, messageId) => {
    const touch = e.targetTouches[0];
    setTouchEnd(null);
    setTouchStart({ x: touch.clientX, y: touch.clientY });
  };

  const onTouchMove = (e) => {
    const touch = e.targetTouches[0];
    setTouchEnd({ x: touch.clientX, y: touch.clientY });
    
    // Prevent scrolling if horizontal swipe is detected
    if (touchStart && touchEnd) {
      const deltaX = Math.abs(touch.clientX - touchStart.x);
      const deltaY = Math.abs(touch.clientY - touchStart.y);
      if (deltaX > deltaY && deltaX > 10) {
        e.preventDefault(); // Prevent scroll during horizontal swipe
      }
    }
  };

  const onTouchEnd = (messageId, isAdminMsg) => {
    if (!touchStart || !touchEnd) {
      setTouchStart(null);
      setTouchEnd(null);
      return;
    }
    
    const deltaX = touchStart.x - touchEnd.x;
    const deltaY = Math.abs(touchStart.y - touchEnd.y);
    const distance = Math.abs(deltaX);
    
    // Only trigger swipe if horizontal movement is greater than vertical
    if (distance > deltaY && distance > minSwipeDistance) {
      const isLeftSwipe = deltaX > 0;
      const isRightSwipe = deltaX < 0;
      
      // For admin messages (right side): swipe left to reveal delete button on left
      // For customer messages (left side): swipe right to reveal delete button on right
      if (isAdminMsg) {
        if (isLeftSwipe) {
          setSwipedMessageId(messageId);
        } else if (isRightSwipe) {
          setSwipedMessageId(null);
        }
      } else {
        if (isRightSwipe) {
          setSwipedMessageId(messageId);
        } else if (isLeftSwipe) {
          setSwipedMessageId(null);
        }
      }
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };


  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return "الآن";
    if (minutes < 60) return `منذ ${minutes} دقيقة`;
    if (hours < 24) return `منذ ${hours} ساعة`;
    if (days < 7) return `منذ ${days} يوم`;
    
    // Format full date for older messages - Gregorian calendar (Algerian format with French-based months in Arabic)
    const arabicMonths = [
      'جانفي', 'فيفري', 'مارس', 'أفريل', 'ماي', 'جوان',
      'جويلية', 'أوت', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
    ];
    
    const day = date.getDate();
    const month = arabicMonths[date.getMonth()];
    const year = date.getFullYear();
    const dateHours = date.getHours();
    const dateMinutes = date.getMinutes();
    const ampm = dateHours >= 12 ? 'م' : 'ص';
    const displayHours = dateHours % 12 || 12;
    const displayMinutes = dateMinutes.toString().padStart(2, '0');
    
    return `${day} ${month} ${year}، ${displayHours}:${displayMinutes} ${ampm}`;
  };

  if (!chatId) {
    return <div style={{ padding: "20px", color: "white" }}>حدد دردشة لعرض الرسائل</div>;
  }

  return (
    <div style={{ 
      backgroundColor: "#0f172a", 
      color: "white", 
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      position: "relative"
    }}>
      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg, rgba(30, 41, 59, 0.98), rgba(51, 65, 85, 0.95))",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(16, 185, 129, 0.2)",
        padding: "16px 20px",
        display: "flex",
        alignItems: "center",
        gap: "16px",
        position: "sticky",
        top: 0,
        zIndex: 100,
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)"
      }}>
        {onBack && (
          <button
            onClick={onBack}
            style={{
              background: "linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(6, 182, 212, 0.2))",
              border: "1px solid rgba(16, 185, 129, 0.4)",
              borderRadius: "50%",
              width: "44px",
              height: "44px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#10b981",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: "0 2px 8px rgba(16, 185, 129, 0.2)",
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.1)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(16, 185, 129, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(16, 185, 129, 0.2)";
            }}
            title="العودة إلى قائمة الدردشات"
          >
            <FaArrowLeft size={18} />
          </button>
        )}
        <div style={{
          background: "linear-gradient(135deg, #10b981, #06b6d4)",
          borderRadius: "50%",
          width: "48px",
          height: "48px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
          flexShrink: 0
        }}>
          <FaUser size={20} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ 
            margin: 0, 
            color: "white", 
            fontSize: "18px",
            fontWeight: "600",
            background: "linear-gradient(135deg, #10b981, #06b6d4)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text"
          }}>
            {customerName || "مستخدم غير معروف"}
          </h2>
          <p style={{ 
            margin: "4px 0 0 0", 
            color: "#94a3b8", 
            fontSize: "13px" 
          }}>
            دردشة مباشرة
          </p>
        </div>
      </div>
      
      {/* Messages Container */}
      <div 
        ref={chatBoxRef}
        style={{ 
          flex: 1,
          overflowY: "auto", 
          padding: "20px",
          background: "transparent",
          paddingBottom: "100px"
        }}
        className="messages-container"
      >
        {messages.map((msg) => {
          const isAdminMessage = msg.sender === "admin";
          const showDeleteButton = hoveredMessageId === msg.id || swipedMessageId === msg.id;
          const isSwiped = swipedMessageId === msg.id;
          const mobileDevice = isMobile();
          
          return (
            <div 
              key={msg.id}
              className="message-wrapper"
              data-message-id={msg.id}
              style={{ 
                marginBottom: "16px",
                display: "flex",
                flexDirection: isAdminMessage ? "row-reverse" : "row",
                alignItems: "flex-end",
                gap: "12px",
                position: "relative",
                transition: "transform 0.3s ease",
                transform: isSwiped ? `translateX(${isAdminMessage ? '50px' : '-50px'})` : 'translateX(0)',
                touchAction: mobileDevice ? "pan-y" : "auto"
              }}
              onMouseEnter={() => !mobileDevice && setHoveredMessageId(msg.id)}
              onMouseLeave={() => !mobileDevice && setHoveredMessageId(null)}
              onTouchStart={(e) => mobileDevice && onTouchStart(e, msg.id)}
              onTouchMove={(e) => mobileDevice && onTouchMove(e)}
              onTouchEnd={() => mobileDevice && onTouchEnd(msg.id, isAdminMessage)}
            >
              {/* Delete button - shown on swipe (mobile) or hover (desktop) */}
              {(isSwiped || (showDeleteButton && !mobileDevice)) && (
                <button
                  className={mobileDevice ? "delete-btn-mobile" : "delete-btn-desktop"}
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteMessage(msg.id);
                  }}
                  disabled={deletingMessageId === msg.id}
                  style={{
                    position: "absolute",
                    [isAdminMessage ? "left" : "right"]: "-50px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: mobileDevice ? "44px" : "36px",
                    height: mobileDevice ? "44px" : "36px",
                    borderRadius: "50%",
                    background: deletingMessageId === msg.id 
                      ? "rgba(107, 114, 128, 0.5)" 
                      : "linear-gradient(135deg, #ef4444, #dc2626)",
                    border: "none",
                    color: "white",
                    cursor: deletingMessageId === msg.id ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.3s ease",
                    opacity: (isSwiped || (showDeleteButton && !mobileDevice)) ? 1 : 0,
                    pointerEvents: "auto",
                    zIndex: 10,
                    boxShadow: "0 2px 8px rgba(239, 68, 68, 0.3)"
                  }}
                  title="حذف الرسالة"
                >
                  {deletingMessageId === msg.id ? (
                    <div style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid rgba(255,255,255,0.3)",
                      borderTopColor: "white",
                      borderRadius: "50%",
                      animation: "spin 0.6s linear infinite"
                    }}></div>
                  ) : (
                    <FaTrash size={mobileDevice ? 16 : 14} />
                  )}
                </button>
              )}
              
              <div 
                className="message-container"
                style={{ 
                  display: "flex", 
                  flexDirection: "column", 
                  maxWidth: mobileDevice ? "85%" : "70%", 
                  alignItems: isAdminMessage ? "flex-end" : "flex-start",
                  width: "100%",
                  position: "relative",
                  zIndex: 1
                }}
              >
              <div
                style={{
                  padding: "12px 16px",
                  borderRadius: "18px",
                  background: msg.sender === "admin" 
                    ? "linear-gradient(135deg, #10b981, #06b6d4)" 
                    : "rgba(51, 65, 85, 0.9)",
                  border: msg.sender === "admin" 
                    ? "none" 
                    : "1px solid rgba(71, 85, 105, 0.5)",
                  color: "white",
                  wordBreak: "break-word",
                  boxShadow: msg.sender === "admin"
                    ? "0 2px 8px rgba(16, 185, 129, 0.2)"
                    : "0 1px 3px rgba(0, 0, 0, 0.15)"
                }}
              >
              {msg.messageType === "voice" && msg.audioData ? (
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "8px",
                  background: "rgba(59, 130, 246, 0.1)",
                  borderRadius: "12px",
                  border: "1px solid rgba(59, 130, 246, 0.3)",
                  minWidth: "200px"
                }}>
                  <button
                    onClick={() => toggleAudio(msg.id, msg.audioData)}
                    style={{
                      background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                      border: "none",
                      borderRadius: "50%",
                      width: "40px",
                      height: "40px",
                      color: "white",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                      boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
                      flexShrink: 0
                    }}
                  >
                    {playingAudioId === msg.id ? (
                      <FaPause size={14} />
                    ) : (
                      <FaPlay size={14} />
                    )}
                  </button>
                  <div style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px"
                  }}>
                    <div style={{
                      fontSize: "14px",
                      fontWeight: "600"
                    }}>🎤 رسالة صوتية</div>
                    {msg.transcribedText && (
                      <div style={{
                        fontSize: "12px",
                        color: "rgba(255, 255, 255, 0.9)",
                        marginTop: "4px",
                        padding: "4px 8px",
                        background: "rgba(16, 185, 129, 0.2)",
                        borderRadius: "6px",
                        fontStyle: "italic"
                      }}>
                        "{msg.transcribedText}"
                      </div>
                    )}
                    {!msg.transcribedText && (
                      <div style={{
                        fontSize: "12px",
                        color: "rgba(255, 255, 255, 0.7)"
                      }}>اضغط للاستماع</div>
                    )}
                  </div>
                  <audio
                    ref={(el) => {
                      if (el && msg.audioData) {
                        audioRefs.current[msg.id] = el;
                        if (!el.src) {
                          el.src = msg.audioData;
                        }
                      }
                    }}
                    onEnded={() => setPlayingAudioId(null)}
                    onPause={() => {
                      if (playingAudioId === msg.id) setPlayingAudioId(null);
                    }}
                  />
                </div>
              ) : (
                <div style={{
                  whiteSpace: "pre-wrap",
                  lineHeight: "1.6",
                  fontSize: "15px"
                }}>
                  {msg.text || ""}
                </div>
              )}
              </div>
              {msg.timestamp && (
                <div style={{
                  fontSize: "10px",
                  color: "rgba(255, 255, 255, 0.5)",
                  marginTop: "4px",
                  padding: "0 4px"
                }}>
                  {formatTimestamp(msg.timestamp)}
                </div>
              )}
            </div>
            <div style={{
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: msg.sender === "admin" 
                ? "linear-gradient(135deg, #10b981, #06b6d4)" 
                : "rgba(107, 114, 128, 0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "16px",
              flexShrink: 0,
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
              border: "2px solid rgba(255, 255, 255, 0.1)"
            }}>
              {msg.sender === "admin" ? (
                <FaRobot size={16} />
              ) : (
                <FaUser size={16} />
              )}
            </div>
          </div>
        );
        })}
      </div>

      {/* Input Area */}
      <div style={{
        borderTop: "1px solid rgba(51, 65, 85, 0.5)",
        padding: "16px",
        background: "linear-gradient(135deg, rgba(15, 23, 42, 0.98), rgba(30, 41, 59, 0.95))",
        backdropFilter: "blur(20px)",
        position: "sticky",
        bottom: 0,
        zIndex: 100
      }}>
        {isRecording && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "12px",
            padding: "12px",
            background: "rgba(239, 68, 68, 0.2)",
            borderRadius: "12px",
            border: "1px solid rgba(239, 68, 68, 0.3)"
          }}>
            <div style={{
              width: "12px",
              height: "12px",
              background: "#ef4444",
              borderRadius: "50%",
              animation: "pulse 1s infinite"
            }}></div>
            <span style={{
              fontFamily: "monospace",
              fontSize: "16px",
              fontWeight: "600"
            }}>
              {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
            </span>
            <span>جاري التسجيل...</span>
          </div>
        )}
        <div style={{
          display: "flex",
          gap: "12px",
          alignItems: "flex-end"
        }}>
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder={isRecording ? "جاري التسجيل..." : "اكتب ردك هنا..."}
            style={{
              flex: 1,
              minHeight: "80px",
              padding: "12px",
              background: "rgba(51, 65, 85, 0.9)",
              border: "2px solid rgba(71, 85, 105, 0.5)",
              borderRadius: "12px",
              color: "white",
              fontSize: "15px",
              resize: "vertical",
              outline: "none",
              ...(isRecording ? { opacity: 0.5, pointerEvents: 'none' } : {})
            }}
            disabled={isRecording}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !isRecording) {
                e.preventDefault();
                sendReply();
              }
            }}
          />
          {!isRecording ? (
            <>
              <button
                onClick={startRecording}
                style={{
                  width: "52px",
                  height: "52px",
                  borderRadius: "50%",
                  background: "rgba(59, 130, 246, 0.2)",
                  border: "2px solid rgba(59, 130, 246, 0.4)",
                  color: "#60a5fa",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)"
                }}
                title="تسجيل رسالة صوتية"
              >
                <FaMicrophone size={18} />
              </button>
              <button
                onClick={sendReply}
                disabled={!reply.trim()}
                style={{
                  width: "52px",
                  height: "52px",
                  borderRadius: "50%",
                  background: reply.trim() 
                    ? "linear-gradient(135deg, #10b981, #06b6d4)" 
                    : "rgba(107, 114, 128, 0.3)",
                  border: "none",
                  color: "white",
                  cursor: reply.trim() ? "pointer" : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s",
                  boxShadow: reply.trim() 
                    ? "0 4px 12px rgba(16, 185, 129, 0.3)" 
                    : "none"
                }}
              >
                <FaPaperPlane size={16} />
              </button>
            </>
          ) : (
            <button
              onClick={stopRecording}
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "50%",
                background: "rgba(239, 68, 68, 0.3)",
                border: "2px solid rgba(239, 68, 68, 0.5)",
                color: "#f87171",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s",
                animation: "pulse 1s infinite"
              }}
              title="إيقاف التسجيل"
            >
              <FaStop size={18} />
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        
        @keyframes spin {
          from { transform: translateY(-50%) rotate(0deg); }
          to { transform: translateY(-50%) rotate(360deg); }
        }
        
        /* Mobile optimizations */
        @media (max-width: 768px) {
          .messages-container {
            padding: 16px !important;
          }
          
          .message-container {
            max-width: 85% !important;
          }
          
          .message-wrapper {
            marginBottom: 12px !important;
            gap: 8px !important;
          }
        }
        
        /* Desktop hover styles */
        @media (min-width: 769px) {
          .message-wrapper:hover .delete-btn-desktop {
            opacity: 1 !important;
            pointer-events: auto !important;
            transform: translateY(-50%) scale(1.1);
          }
        }
        
        /* Better scrollbar */
        .messages-container::-webkit-scrollbar {
          width: 6px;
        }
        
        .messages-container::-webkit-scrollbar-track {
          background: rgba(51, 65, 85, 0.3);
          border-radius: 10px;
        }
        
        .messages-container::-webkit-scrollbar-thumb {
          background: rgba(16, 185, 129, 0.5);
          border-radius: 10px;
        }
        
        .messages-container::-webkit-scrollbar-thumb:hover {
          background: rgba(16, 185, 129, 0.7);
        }
      `}</style>
    </div>
  );
};

export default AdminChatScreen;
